import type { ReactNode } from 'react';
import { CheckIcon } from '@phosphor-icons/react';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import type { BusLegStepView } from '../models.js';
import './bus_leg_stepper.css';

export type BusLegStepperCopy = {
  /** O nome acessível da fila ("Pernas da viagem"). */
  label: string;
  /** "Ida escolhida" — a bolha do passo concluído troca o número pelo tique e
   *  reempossa por rótulo o que o número dizia. */
  doneStepLabel(step: number): string;
};

/** A bolha: o número do passo, ou o tique quando ele já foi escolhido. */
function StepBubble({
  n,
  done,
  label,
}: {
  readonly n: number;
  readonly done: boolean;
  readonly label: string;
}): ReactNode {
  return (
    <span
      className="hui-bus-legs-bubble"
      data-done={done || undefined}
      {...(done ? { role: 'img', 'aria-label': label } : {})}
    >
      {done ? <CheckIcon aria-hidden="true" /> : n}
    </span>
  );
}

/** A logo da viação no passo concluído; quebrada, some — a linha já nomeia
 *  a viação no texto. */
function StepLogo({ logo }: { readonly logo: { src: string; alt: string } }): ReactNode {
  const image = useImageFallback(logo.src);
  if (!image.src) return null;
  return (
    <img
      src={image.src}
      alt={logo.alt}
      onError={image.onError}
      className="mr-1.5 inline-block h-3.5 w-12 object-contain object-left align-text-bottom"
    />
  );
}

/** O corpo de um passo: a bolha, a rota e a linha de baixo. */
function StepBody({
  n,
  step,
  copy,
}: {
  readonly n: number;
  readonly step: BusLegStepView;
  readonly copy: BusLegStepperCopy;
}): ReactNode {
  return (
    <>
      <StepBubble n={n} done={step.status === 'done'} label={copy.doneStepLabel(n)} />
      <span className="hui-bus-legs-text">
        <span className="hui-bus-legs-route">{step.route}</span>
        <span className="hui-bus-legs-detail">
          {step.logo && <StepLogo logo={step.logo} />}
          {step.detail}
        </span>
      </span>
    </>
  );
}

/**
 * O stepper de pernas da disponibilidade (#2440, a convenção da #2363): ida e,
 * no ida-e-volta, volta. É stepper e não aba: há ordem e estado por passo. O
 * passo em cena tem o anel (`aria-current="step"`); um passo concluído é botão
 * — voltar a ele é trocar a escolha daquela perna.
 */
export function BusLegStepper({
  steps,
  copy,
  disabled = false,
  onStepSelect,
}: {
  readonly steps: readonly BusLegStepView[];
  readonly copy: BusLegStepperCopy;
  readonly disabled?: boolean;
  readonly onStepSelect?: (index: number) => void;
}): ReactNode {
  return (
    <ol className="hui-bus-legs" aria-label={copy.label}>
      {steps.map((step, index) => {
        const n = index + 1;
        const body = <StepBody n={n} step={step} copy={copy} />;
        return (
          <li
            key={step.route}
            className="hui-bus-legs-step"
            data-status={step.status}
            aria-current={step.status === 'current' ? 'step' : undefined}
          >
            {step.status === 'done' && onStepSelect ? (
              <button
                type="button"
                className="hui-bus-legs-hit"
                disabled={disabled}
                onClick={() => {
                  onStepSelect(index);
                }}
              >
                {body}
              </button>
            ) : (
              <div className="hui-bus-legs-hit">{body}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
